import React, { useState, useEffect } from 'react';
import MongoDBConnection from '../../../models/Connections/MongoDBConnection';
import { 
  Card, 
  CardContent, 
  TextField, 
  Switch, 
  FormControlLabel,
  Divider, 
  Typography,
  Box,
  IconButton,
  Collapse,
  Alert
} from '@mui/material';
import { Database } from 'lucide-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface MongoDBConnectionBoxProps {
  mongodbConnectionData: MongoDBConnection;
  onUpdate?: (connection: MongoDBConnection) => void;
}

const MongoDBConnectionBox: React.FC<MongoDBConnectionBoxProps> = ({ 
  mongodbConnectionData, 
  onUpdate 
}) => {
  const [data, setData] = useState<MongoDBConnection>(mongodbConnectionData);
  const [collectionName, setCollectionName] = useState<string>(
    data.connectionArgs.collection_name || ''
  );
  const [connectionName, setConnectionName] = useState<string>(
    data.connectionArgs.connection_name || ''
  );
  const [query, setQuery] = useState<string>(data.connectionArgs.query || '');
  const [filter, setFilter] = useState<Record<string, any>>(data.connectionArgs.filter || {});
  const [filterInput, setFilterInput] = useState<string>(
    JSON.stringify(data.connectionArgs.filter || {}, null, 2)
  );
  const [retrieve, setRetrieve] = useState<boolean>(data.connectionArgs.retrieve ?? true);
  const [learn, setLearn] = useState<boolean>(data.connectionArgs.learn ?? false);
  const [error, setError] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  useEffect(() => {
    const updatedData = new MongoDBConnection({
      collection_name: collectionName,
      connection_name: connectionName,
      query,
      filter,
      // retrieve and learn properties removed - not part of MongoDBConnectionArgs
    });
    updatedData.id = mongodbConnectionData.id;
    setData(updatedData);

    if (onUpdate) {
      onUpdate(updatedData);
    }
  }, [collectionName, connectionName, query, filter, retrieve, learn, mongodbConnectionData.id, onUpdate]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setFilterInput(value);

    try {
      const parsedValue = value === 'null' ? null : JSON.parse(value);
      setFilter(parsedValue);
      setError('');
    } catch (err) {
      setError('Invalid JSON format');
    }
  };

  return (
    <Card variant="outlined" sx={{ borderColor: 'green.300', borderWidth: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              size="small"
            >
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
            <Database size={20} color="#4caf50" />
            <Typography variant="h6" fontWeight="bold">
              MongoDB JSON Connection
            </Typography>
          </Box>
        </Box>

        <Collapse in={!isCollapsed}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Connection Properties */}
            <Card variant="outlined" sx={{ bgcolor: 'green.50' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Connection Properties
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={retrieve}
                        onChange={(e) => setRetrieve(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Retrieve</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enable information retrieval from this connection
                        </Typography>
                      </Box>
                    }
                  />
                  
                  <Divider />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={learn}
                        onChange={(e) => setLearn(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Learn</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enable learning and storing new information
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </CardContent>
            </Card>

            <TextField
              label="Connection Name"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              placeholder="default_connection"
              fullWidth
              size="small"
              helperText="Name identifier for this connection"
            />

            <TextField
              label="Collection Name"
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              placeholder="documents"
              fullWidth
              size="small"
              helperText="Name of the MongoDB collection to search"
            />

            <TextField
              label="Query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              multiline
              rows={3}
              fullWidth
              size="small"
              placeholder="Enter your search query here"
              helperText="Search query to execute against the collection"
              sx={{ fontFamily: 'monospace' }}
            />

            <TextField
              label="Filter (JSON)"
              value={filterInput}
              onChange={handleFilterChange}
              multiline
              rows={4}
              fullWidth
              size="small"
              placeholder='{"status": "active", "category": "documents"}'
              error={!!error}
              helperText={error || "MongoDB query filter to apply before search"}
              sx={{ fontFamily: 'monospace' }}
            />
            
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default MongoDBConnectionBox;